import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CollectionsOutlined as GalleryIcon,
  Edit as EditIcon,
  ImageNotSupportedOutlined as NoThumbIcon,
  LinkOutlined as LinkIcon,
  OpenInNew as OpenInNewIcon,
  PersonAddAlt1Outlined as PersonAddIcon,
  Refresh as RefreshIcon,
  ViewInArOutlined as ViewerIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import IdocViewer from './IdocViewer';
import IdocConnectDialog from './IdocConnectDialog';
import {
  idocService,
  openIdocDocInPortal,
  type IdocApiError,
  type IdocPatientDoc,
  type IdocPatientDocsResult,
  type IdocViewerSessionsResult,
} from '../../services/idocService';

const ACCENT = '#0D9488';
const MAX_THUMBS = 6;

const formatDocDate = (iso: string | null) => {
  if (!iso) return 'Sem data';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Sem data' : format(date, "dd 'de' MMM yyyy", { locale: ptBR });
};

function typeSummary(doc: IdocPatientDoc): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const item of doc.items) {
    const label = (item.type || item.name || 'Item').trim();
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return Array.from(counts.entries()).map(([label, count]) => ({ label, count })).slice(0, 4);
}

interface PatientIdocTabProps {
  patientId: string;
  patientName?: string | null;
  /** Pode conectar contas e criar o paciente no iDoc (admin ou profissional). */
  canManage: boolean;
  onEditPatient?: () => void;
  /** Chamado após a lista carregar: as docs novas viram "vistas". */
  onSeen?: () => void;
  /** Dentro da aba Imagens do painel — sem padding extra. */
  embedded?: boolean;
}

const PatientIdocTab: React.FC<PatientIdocTabProps> = ({ patientId, patientName, canManage, onEditPatient, onSeen, embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [connectOpen, setConnectOpen] = useState(false);

  const [view, setView] = useState<'gallery' | 'viewer'>('gallery');
  const [result, setResult] = useState<IdocPatientDocsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<IdocApiError | null>(null);
  const [showOtherNames, setShowOtherNames] = useState(false);
  const [busyDoc, setBusyDoc] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdMessage, setCreatedMessage] = useState<string | null>(null);

  const [patientViewer, setPatientViewer] = useState<IdocViewerSessionsResult | null>(null);
  const [patientViewerLoading, setPatientViewerLoading] = useState(false);
  const [patientViewerError, setPatientViewerError] = useState<string | null>(null);
  const [sessionIndex, setSessionIndex] = useState(0);

  const [docViewer, setDocViewer] = useState<{ doc: IdocPatientDoc; data: IdocViewerSessionsResult } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await idocService.getPatientDocs(patientId);
      setResult(data);
      onSeen?.();
    } catch (e) {
      setError(e as IdocApiError);
    } finally {
      setLoading(false);
    }
  }, [patientId, onSeen]);

  useEffect(() => {
    setResult(null);
    setPatientViewer(null);
    setShowOtherNames(false);
    void load();
  }, [load]);

  const loadPatientViewer = useCallback(async () => {
    setPatientViewerLoading(true);
    setPatientViewerError(null);
    try {
      const data = await idocService.startPatientViewer(patientId);
      setPatientViewer(data);
      setSessionIndex(0);
    } catch (e) {
      setPatientViewerError((e as IdocApiError).message);
    } finally {
      setPatientViewerLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (view === 'viewer' && !patientViewer && !patientViewerLoading && !patientViewerError) {
      void loadPatientViewer();
    }
  }, [view, patientViewer, patientViewerLoading, patientViewerError, loadPatientViewer]);

  const docs = useMemo(() => result?.docs || [], [result]);
  const ownDocs = useMemo(() => docs.filter((d) => d.name_matches), [docs]);
  const otherDocs = useMemo(() => docs.filter((d) => !d.name_matches), [docs]);
  const visibleDocs = showOtherNames ? docs : ownDocs;
  const multipleAccounts = (result?.accounts_count || 0) > 1;

  const openPortal = async (doc: IdocPatientDoc) => {
    setActionError(null);
    setBusyDoc(`portal:${doc.cod_doc}`);
    try {
      await openIdocDocInPortal(doc.cod_doc, doc.account_id);
    } catch (e) {
      setActionError((e as IdocApiError).message);
    } finally {
      setBusyDoc(null);
    }
  };

  const openDocViewer = async (doc: IdocPatientDoc) => {
    setActionError(null);
    setBusyDoc(`viewer:${doc.cod_doc}`);
    try {
      const data = await idocService.startDocViewer(doc.cod_doc, doc.account_id);
      setDocViewer({ doc, data });
    } catch (e) {
      setActionError((e as IdocApiError).message);
    } finally {
      setBusyDoc(null);
    }
  };

  const createInIdoc = async () => {
    setCreating(true);
    setActionError(null);
    try {
      const created = await idocService.createPatient(patientId);
      setCreatedMessage(
        created.already_existed
          ? 'O paciente já tinha conta no iDoc; uma documentação vinculada a você foi criada.'
          : 'Paciente criado no iDoc com uma documentação vazia, pronta para receber imagens.',
      );
      await load();
    } catch (e) {
      setActionError((e as IdocApiError).message);
    } finally {
      setCreating(false);
    }
  };

  const header = (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewerIcon sx={{ color: ACCENT }} /> Radiologia iDoc
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Exames, imagens e laudos recebidos pela Radio Memory
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, gap: 0.75 }, '& .Mui-selected': { color: `${ACCENT} !important`, bgcolor: 'rgba(13,148,136,0.08) !important' } }}
        >
          <ToggleButton value="gallery"><GalleryIcon fontSize="small" /> Galeria</ToggleButton>
          <ToggleButton value="viewer"><ViewerIcon fontSize="small" /> Visualizador</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Atualizar">
          <span>
            <IconButton
              size="small"
              disabled={loading || patientViewerLoading}
              onClick={() => {
                if (view === 'viewer') {
                  setPatientViewer(null);
                  setPatientViewerError(null);
                } else {
                  void load();
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  const issues = (result?.errors || []).length > 0 && (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {result!.errors.map((issue) => (
        <Alert
          key={issue.account_id}
          severity="warning"
          action={canManage ? (
            <Button size="small" color="inherit" onClick={() => setConnectOpen(true)}>Reconectar</Button>
          ) : undefined}
        >
          <strong>{issue.label}:</strong> {issue.message}
        </Alert>
      ))}
    </Stack>
  );

  const renderEmpty = () => {
    if (!result) return null;
    if (result.status === 'no_account') {
      return (
        <EmptyState
          title="Nenhuma conta iDoc conectada"
          description={canManage
            ? 'Conecte a conta iDoc do dentista para ver aqui as documentações dos pacientes.'
            : 'Peça a um administrador ou ao dentista para conectar a conta iDoc em Configurações da clínica.'}
          action={canManage ? (
            <Button variant="contained" startIcon={<LinkIcon />} onClick={() => setConnectOpen(true)} sx={primaryButtonSx}>
              Conectar conta iDoc
            </Button>
          ) : null}
        />
      );
    }
    if (result.status === 'missing_criteria') {
      return (
        <EmptyState
          title="Faltam dados para buscar no iDoc"
          description="O iDoc encontra o paciente por CPF, e-mail ou pelo conjunto nome + data de nascimento + sexo. Complete o cadastro para buscar."
          action={onEditPatient ? (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={onEditPatient} sx={outlinedButtonSx}>Completar cadastro</Button>
          ) : null}
        />
      );
    }
    const c = result.criteria;
    return (
      <EmptyState
        title="Nenhuma documentação encontrada"
        description="Docs que não foram solicitadas por este dentista só aparecem depois que o paciente as compartilha pela conta dele no iDoc."
        extra={(
          <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <CriteriaChip label="CPF" used={c.cpf} />
            <CriteriaChip label="E-mail" used={c.email} />
            <CriteriaChip label="Nome + nascimento + sexo" used={c.nameBirthSex} />
          </Stack>
        )}
        action={canManage ? (
          result.missing_for_create.length === 0 ? (
            <Button variant="outlined" startIcon={creating ? <CircularProgress size={16} /> : <PersonAddIcon />} disabled={creating} onClick={createInIdoc} sx={outlinedButtonSx}>
              Criar paciente no iDoc
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Para criar o paciente no iDoc, cadastre: {result.missing_for_create.join(', ')}.
            </Typography>
          )
        ) : null}
      />
    );
  };

  const gallery = (
    <>
      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Card key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <Skeleton width={160} height={22} />
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                {[0, 1, 2, 3].map((j) => <Skeleton key={j} variant="rounded" width={96} height={72} />)}
              </Box>
            </Card>
          ))}
        </Stack>
      ) : error ? (
        <Alert severity={error.code === 'IDOC_DISABLED' ? 'info' : 'error'} action={<Button color="inherit" size="small" onClick={() => void load()}>Tentar de novo</Button>}>
          {error.message}
        </Alert>
      ) : !visibleDocs.length && !otherDocs.length ? (
        renderEmpty()
      ) : (
        <>
          {otherDocs.length > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => setShowOtherNames((v) => !v)}>{showOtherNames ? 'Ocultar' : 'Mostrar'}</Button>}
            >
              {otherDocs.length === 1 ? '1 documentação' : `${otherDocs.length} documentações`} com outro nome no iDoc
              (comum quando pais e filhos usam o mesmo e-mail na radiologia).
            </Alert>
          )}
          {!visibleDocs.length ? renderEmpty() : (
            <Stack spacing={1.5}>
              {visibleDocs.map((doc) => (
                <DocCard
                  key={`${doc.account_id}:${doc.cod_doc}`}
                  doc={doc}
                  patientName={patientName}
                  showAccount={multipleAccounts}
                  busyDoc={busyDoc}
                  onView={() => void openDocViewer(doc)}
                  onPortal={() => void openPortal(doc)}
                />
              ))}
            </Stack>
          )}
        </>
      )}
    </>
  );

  const sessions = patientViewer?.sessions || [];
  const activeSession = sessions[sessionIndex];
  const viewer = patientViewerLoading ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
  ) : patientViewerError ? (
    <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => setPatientViewerError(null)}>Tentar de novo</Button>}>
      {patientViewerError}
    </Alert>
  ) : patientViewer && !sessions.length ? (
    <EmptyState title="Nenhum histórico no iDoc" description="O visualizador não encontrou documentações para este paciente." />
  ) : activeSession ? (
    <>
      {sessions.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          {sessions.map((s, i) => (
            <Chip
              key={`${s.account_id}-${i}`}
              label={s.patient_name ? `${s.account_label} · ${s.patient_name}` : s.account_label}
              onClick={() => setSessionIndex(i)}
              color={i === sessionIndex ? 'primary' : 'default'}
              variant={i === sessionIndex ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>
      )}
      <IdocViewer
        key={`${activeSession.account_id}-${sessionIndex}`}
        scriptUrl={patientViewer!.script_url}
        session={activeSession.session}
        minHeight={isMobile ? 420 : 600}
        onOpenPortal={ownDocs[0] ? () => void openPortal(ownDocs[0]) : undefined}
      />
    </>
  ) : null;

  return (
    <Box sx={embedded ? undefined : { p: { xs: 1.5, sm: 2, md: 3 } }}>
      {header}
      {createdMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreatedMessage(null)}>{createdMessage}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      {issues}
      {view === 'gallery' ? gallery : viewer}

      <IdocConnectDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnected={() => {
          setPatientViewer(null);
          setPatientViewerError(null);
          void load();
        }}
      />

      <Dialog
        open={Boolean(docViewer)}
        onClose={() => setDocViewer(null)}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, height: isMobile ? '100%' : '88vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <ViewerIcon sx={{ color: ACCENT }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }} noWrap>
              Documentação de {formatDocDate(docViewer?.doc.updated_at || docViewer?.doc.created_at || null)}
            </Typography>
            {docViewer?.doc.patient_name && (
              <Typography variant="caption" color="text.secondary" noWrap>{docViewer.doc.patient_name}</Typography>
            )}
          </Box>
          {docViewer && (
            <Tooltip title="Abrir no portal iDoc">
              <IconButton onClick={() => void openPortal(docViewer.doc)}><OpenInNewIcon /></IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => setDocViewer(null)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          {docViewer?.data.sessions[0] && (
            <IdocViewer
              scriptUrl={docViewer.data.script_url}
              session={docViewer.data.sessions[0].session}
              minHeight="calc(88vh - 130px)"
              onOpenPortal={() => void openPortal(docViewer.doc)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const primaryButtonSx = { bgcolor: ACCENT, '&:hover': { bgcolor: '#0F766E' }, textTransform: 'none', borderRadius: 2 } as const;
const outlinedButtonSx = { borderColor: ACCENT, color: ACCENT, textTransform: 'none', borderRadius: 2 } as const;

const CriteriaChip: React.FC<{ label: string; used: boolean }> = ({ label, used }) => (
  <Chip
    size="small"
    label={used ? `${label} ✓` : `${label} — não cadastrado`}
    variant={used ? 'filled' : 'outlined'}
    sx={used ? { bgcolor: 'rgba(13,148,136,0.1)', color: '#0F766E', fontWeight: 600 } : { color: '#94a3b8' }}
  />
);

const EmptyState: React.FC<{ title: string; description: string; action?: React.ReactNode; extra?: React.ReactNode }> = ({ title, description, action, extra }) => (
  <Box sx={{ textAlign: 'center', py: 6, px: 2, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
    <ViewerIcon sx={{ fontSize: 52, color: '#cbd5e1', mb: 1 }} />
    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520, mx: 'auto' }}>{description}</Typography>
    {extra}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

interface DocCardProps {
  doc: IdocPatientDoc;
  patientName?: string | null;
  showAccount: boolean;
  busyDoc: string | null;
  onView: () => void;
  onPortal: () => void;
}

const DocCard: React.FC<DocCardProps> = ({ doc, patientName, showAccount, busyDoc, onView, onPortal }) => {
  const thumbs = doc.items.filter((i) => i.thumbnail_url);
  const extra = Math.max(0, doc.items.length - Math.min(thumbs.length, MAX_THUMBS));
  const summary = typeSummary(doc);
  const viewing = busyDoc === `viewer:${doc.cod_doc}`;
  const opening = busyDoc === `portal:${doc.cod_doc}`;

  return (
    <Card
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: doc.is_new ? 'rgba(13,148,136,0.45)' : '#e2e8f0',
        boxShadow: doc.is_new ? '0 0 0 3px rgba(13,148,136,0.08)' : 'none',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: ACCENT, boxShadow: '0 4px 14px rgba(15,23,42,0.08)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{formatDocDate(doc.updated_at || doc.created_at)}</Typography>
            {doc.is_new && <Chip label="Novo" size="small" sx={{ height: 20, bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />}
            {!doc.name_matches && doc.patient_name && (
              <Chip label={`Nome no iDoc: ${doc.patient_name}`} size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {doc.items.length === 1 ? '1 item' : `${doc.items.length} itens`}
            {showAccount ? ` · ${doc.account_label}` : ''}
            {doc.name_matches && doc.patient_name && patientName && doc.patient_name.toLowerCase() !== patientName.toLowerCase()
              ? ` · no iDoc como ${doc.patient_name}`
              : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={viewing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <VisibilityIcon />}
            disabled={Boolean(busyDoc)}
            onClick={onView}
            sx={primaryButtonSx}
          >
            Visualizar
          </Button>
          <Tooltip title="Abrir no portal iDoc (nova aba)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={opening ? <CircularProgress size={14} /> : <OpenInNewIcon />}
                disabled={Boolean(busyDoc)}
                onClick={onPortal}
                sx={outlinedButtonSx}
              >
                Portal
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {(thumbs.length > 0 || doc.items.length > 0) && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, overflowX: 'auto', pb: 0.5 }}>
          {thumbs.slice(0, MAX_THUMBS).map((item, i) => (
            <Tooltip key={`${item.cod_item || i}`} title={item.name || item.type || ''}>
              <Box
                onClick={onView}
                sx={{ flex: '0 0 auto', width: 104, height: 78, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#0f172a', cursor: 'pointer', position: 'relative', border: '1px solid #e2e8f0', '&:hover img': { transform: 'scale(1.05)' } }}
              >
                <img
                  src={item.thumbnail_url as string}
                  alt={item.name || 'Imagem iDoc'}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .2s' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </Box>
            </Tooltip>
          ))}
          {thumbs.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#94a3b8' }}>
              <NoThumbIcon fontSize="small" />
              <Typography variant="caption">Sem miniaturas — abra para visualizar</Typography>
            </Box>
          )}
          {extra > 0 && thumbs.length > 0 && (
            <Box onClick={onView} sx={{ flex: '0 0 auto', width: 78, height: 78, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569', fontWeight: 700 }}>
              +{extra}
            </Box>
          )}
        </Box>
      )}

      {summary.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          {summary.map((s) => (
            <Chip key={s.label} size="small" label={s.count > 1 ? `${s.label} · ${s.count}` : s.label} sx={{ height: 22, bgcolor: '#f1f5f9', color: '#334155' }} />
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default PatientIdocTab;
